/** Giả lập xử lý AI cho demo UX — không gọi backend */

export const simulateProcessing = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export type TimelineEventType = 'normal' | 'conflict' | 'warning'

export interface AnalysisTimelineItem {
  date: string
  event: string
  type: TimelineEventType
  source: string
}

export interface AnalysisConflict {
  id: number
  severity: 'high' | 'medium' | 'low'
  desc: string
  locationA: { page: number; line: number }
  locationB: { page: number; line: number }
}

export interface AnalyzeDocumentResult {
  timeline: AnalysisTimelineItem[]
  conflicts: AnalysisConflict[]
  summary: string
  riskScore: number
}

export async function analyzeDocument(_files: File[]): Promise<AnalyzeDocumentResult> {
  await simulateProcessing(2500)
  return {
    timeline: [
      { date: '01/2023', event: 'Ký kết hợp đồng', type: 'normal', source: 'HĐ-001.pdf' },
      { date: '03/2023', event: 'Vi phạm điều khoản 5', type: 'conflict', source: 'Email-032023.eml' },
      { date: '06/2023', event: 'Khiếu nại lần 1', type: 'warning', source: 'BB-062023.pdf' },
    ],
    conflicts: [
      {
        id: 1,
        severity: 'high',
        desc: 'Điều 7 ↔ Điều 12: Ngày hiệu lực không khớp',
        locationA: { page: 3, line: 12 },
        locationB: { page: 7, line: 4 },
      },
      {
        id: 2,
        severity: 'medium',
        desc: 'Phụ lục A ↔ Hợp đồng chính: Giá trị sai lệch 50tr',
        locationA: { page: 1, line: 8 },
        locationB: { page: 12, line: 1 },
      },
    ],
    summary: 'Tài liệu có 2 rủi ro cao và 1 rủi ro trung bình cần xem xét.',
    riskScore: 72,
  }
}

export interface RiskItem {
  id: number
  level: 'high' | 'medium' | 'low'
  clause: string
  title: string
  original: string
  issue: string
  suggestion: string
  position: { start: number; end: number }
}

export interface ReviewContractResult {
  totalClauses: number
  riskItems: RiskItem[]
}

export const DEMO_CONTRACT = `HỢP ĐỒNG CUNG CẤP DỊCH VỤ PHÁP LÝ (Mẫu tham khảo)

HỢP ĐỒNG DỊCH VỤ PHÁP LÝ
(V/v: Đại diện giải quyết tranh chấp HĐMB căn hộ ABC)

Số: 05/2026/HĐDVPL-ABC
Hôm nay, ngày 02/04/2026, tại TP.HCM, chúng tôi gồm:

1) BÊN A (KHÁCH HÀNG): Ông Nguyễn Văn A, CCCD: 0790xxxxxxx, địa chỉ: Quận 7, TP.HCM.
2) BÊN B (ĐƠN VỊ CUNG CẤP DỊCH VỤ): Công ty Luật XYZ, MST: 031xxxxxxx, địa chỉ: Quận 1, TP.HCM.

Điều 1. Phạm vi dịch vụ
1.1. Bên B tư vấn chiến lược, soạn thảo đơn từ, thu thập – sắp xếp chứng cứ, và tham gia tố tụng để bảo vệ quyền lợi Bên A trong tranh chấp HĐMB căn hộ dự án ABC.
1.2. Bên B không cam kết kết quả tố tụng; kết quả phụ thuộc Tòa án và chứng cứ.

Điều 2. Thời hạn dịch vụ
2.1. Thời hạn thực hiện: từ ngày ký đến khi có bản án/quyết định có hiệu lực hoặc theo thỏa thuận khác.

Điều 3. Phí dịch vụ và phương thức thanh toán
3.1. Phí dịch vụ cố định: 120.000.000 VNĐ (đã gồm VAT).
3.2. Phí theo kết quả (success fee): 25% giá trị nghĩa vụ tài chính mà Bên A thu hồi/giảm trừ được theo bản án/quyết định.
3.3. Bên A thanh toán 100% phí cố định trong vòng 03 ngày kể từ ngày ký hợp đồng. Bên B chỉ bắt đầu thực hiện sau khi nhận đủ tiền.
3.4. Mọi chi phí án phí, lệ phí, đi lại, sao lục, công chứng... do Bên A chịu và thanh toán theo yêu cầu phát sinh.

Điều 4. Nghĩa vụ cung cấp thông tin và chứng cứ
4.1. Bên A cam kết cung cấp đầy đủ, trung thực tài liệu: HĐMB, phụ lục, email trao đổi, biên bản bàn giao, chứng từ thanh toán.
4.2. Nếu Bên A cung cấp thiếu hoặc sai lệch thông tin làm ảnh hưởng kết quả, Bên B không chịu trách nhiệm.

Điều 5. Chấm dứt hợp đồng
5.1. Bên B có quyền đơn phương chấm dứt hợp đồng bất kỳ lúc nào mà không cần nêu lý do.
5.2. Trường hợp chấm dứt theo Điều 5.1, Bên B không hoàn trả bất kỳ khoản phí nào đã nhận.

Điều 6. Phạt vi phạm và bồi thường
6.1. Nếu Bên A chậm thanh toán, Bên A chịu phạt 0,5%/ngày trên số tiền chậm trả.
6.2. Mức phạt vi phạm là 50% giá trị hợp đồng.

Điều 7. Bảo mật
7.1. Hai bên có trách nhiệm bảo mật thông tin vụ việc và tài liệu.

Điều 8. Giải quyết tranh chấp
8.1. Mọi tranh chấp phát sinh từ hợp đồng này được giải quyết tại Trung tâm Trọng tài Quốc tế Việt Nam (VIAC).

Điều 12 – Chấm dứt hợp đồng
Các bên có quyền chấm dứt hợp đồng bất kỳ lúc nào.

(Các điều khoản khác được rút gọn để minh họa.)`

function posFor(text: string, needle: string): { start: number; end: number } {
  const i = text.indexOf(needle)
  if (i < 0) return { start: 0, end: Math.min(needle.length, text.length) }
  return { start: i, end: i + needle.length }
}

export async function reviewContract(content: string): Promise<ReviewContractResult> {
  await simulateProcessing(1800)
  const base = content.trim().length > 50 ? content : DEMO_CONTRACT
  const o1 = 'Bên A thanh toán 100% phí cố định trong vòng 03 ngày kể từ ngày ký hợp đồng. Bên B chỉ bắt đầu thực hiện sau khi nhận đủ tiền.'
  const o2 = 'Phí theo kết quả (success fee): 25% giá trị nghĩa vụ tài chính mà Bên A thu hồi/giảm trừ được theo bản án/quyết định.'
  const o3 = 'Bên B có quyền đơn phương chấm dứt hợp đồng bất kỳ lúc nào mà không cần nêu lý do.'
  const o4 = 'Trường hợp chấm dứt theo Điều 5.1, Bên B không hoàn trả bất kỳ khoản phí nào đã nhận.'
  const o5 = 'Mức phạt vi phạm là 50% giá trị hợp đồng.'
  const o6 = 'Mọi tranh chấp phát sinh từ hợp đồng này được giải quyết tại Trung tâm Trọng tài Quốc tế Việt Nam (VIAC).'
  return {
    totalClauses: 24,
    riskItems: [
      {
        id: 1,
        level: 'high',
        clause: 'Điều 6.2',
        title: 'Phạt vi phạm quá cao/khó bảo vệ',
        original: o5,
        issue: 'Mức phạt 50% thường bị xem là bất hợp lý; dễ bị tranh luận giảm/không được chấp nhận toàn bộ khi có tranh chấp.',
        suggestion: 'Điều chỉnh: “Mức phạt vi phạm không vượt quá 8% giá trị nghĩa vụ vi phạm; thiệt hại thực tế được bồi thường theo chứng cứ.”',
        position: posFor(base, o5),
      },
      {
        id: 2,
        level: 'medium',
        clause: 'Điều 3.3',
        title: 'Thanh toán 100% trước khi thực hiện',
        original: o1,
        issue: 'Dồn rủi ro về phía khách hàng; không có cơ chế nghiệm thu theo mốc công việc/hoàn tiền tương ứng.',
        suggestion: 'Chia thành 3 đợt: 40% khi ký, 30% sau khi nộp đơn/hoàn tất hồ sơ, 30% sau khi tham gia phiên làm việc/phiên tòa (hoặc theo milestone).',
        position: posFor(base, o1),
      },
      {
        id: 3,
        level: 'high',
        clause: 'Điều 5.1',
        title: 'Quyền chấm dứt một chiều của bên cung cấp',
        original: o3,
        issue: 'Thiếu điều kiện chấm dứt hợp lý (ví dụ: khách hàng vi phạm nghĩa vụ, xung đột lợi ích, bất khả kháng); rủi ro bỏ dở vụ việc.',
        suggestion: 'Quy định điều kiện chấm dứt + nghĩa vụ bàn giao hồ sơ + hỗ trợ chuyển giao + thông báo trước tối thiểu 15 ngày.',
        position: posFor(base, o3),
      },
      {
        id: 4,
        level: 'high',
        clause: 'Điều 5.2',
        title: 'Không hoàn phí trong mọi trường hợp',
        original: o4,
        issue: 'Điều khoản “không hoàn trả bất kỳ khoản phí nào” dễ gây tranh chấp; thiếu cơ chế hoàn phí theo phần công việc chưa thực hiện.',
        suggestion: 'Hoàn phí theo tỷ lệ phần việc/mốc nghiệm thu chưa thực hiện; khấu trừ chi phí hợp lý đã phát sinh có chứng từ.',
        position: posFor(base, o4),
      },
      {
        id: 5,
        level: 'medium',
        clause: 'Điều 3.2',
        title: 'Success fee mô tả chưa rõ cách tính',
        original: o2,
        issue: '“giá trị nghĩa vụ tài chính thu hồi/giảm trừ” có thể diễn giải khác nhau; thiếu ví dụ minh họa, thời điểm xác định và cơ chế đối soát.',
        suggestion: 'Bổ sung công thức + ví dụ: success fee tính trên phần chênh lệch nghĩa vụ giảm trừ theo bản án có hiệu lực; thanh toán trong 07 ngày sau khi đối soát biên bản.',
        position: posFor(base, o2),
      },
      {
        id: 6,
        level: 'low',
        clause: 'Điều 8.1',
        title: 'Thỏa thuận trọng tài có thể không phù hợp',
        original: o6,
        issue: 'Nếu hợp đồng dịch vụ có yếu tố không thuộc thẩm quyền trọng tài hoặc khách hàng không muốn trọng tài (chi phí/thi hành), điều khoản này có thể gây bất tiện.',
        suggestion: 'Cho phép lựa chọn: Tòa án có thẩm quyền tại TP.HCM hoặc VIAC theo thỏa thuận; nêu rõ luật áp dụng và địa điểm.',
        position: posFor(base, o6),
      },
    ],
  }
}

export interface RedTeamWeakness {
  severity: 'high' | 'medium' | 'low'
  point: string
  detail: string
  suggestion: string
}

export interface RedTeamResult {
  weaknesses: RedTeamWeakness[]
  riskForecast: {
    winFull: number
    winPartial: number
    lose: number
    basedOn: number
    keyFactors: { factor: string; impact: number }[]
  }
  strategySuggestions: string[]
}

export async function runRedTeamAnalysis(_caseInfo: string, _userArgument: string): Promise<RedTeamResult> {
  await simulateProcessing(2200)
  return {
    weaknesses: [
      {
        severity: 'high',
        point: 'Chưa làm rõ nguồn tiền và chứng cứ dòng tiền',
        detail:
          'Bên bị đơn có thể lập luận căn hộ được mua từ tài sản riêng trước hôn nhân (Điều 43 Luật HN&GĐ 2014) nếu hồ sơ chưa có sao kê/ủy nhiệm chi thể hiện nguồn tiền chung.',
        suggestion: 'Bổ sung sao kê, ủy nhiệm chi, chứng từ thanh toán và tài liệu thể hiện công sức đóng góp của mỗi bên',
      },
      {
        severity: 'medium',
        point: 'Chưa chuẩn bị hướng xử lý khi đối phương viện dẫn án lệ',
        detail: 'Đối phương có thể viện dẫn Án lệ 08/2019/AL để tranh luận về nghĩa vụ chứng minh và nguồn gốc tài sản.',
        suggestion: 'Chuẩn bị luận cứ phân biệt tình tiết vụ án và hệ thống hóa chứng cứ theo từng mốc thời gian',
      },
    ],
    riskForecast: {
      winFull: 35,
      winPartial: 55,
      lose: 10,
      basedOn: 847,
      keyFactors: [
        { factor: 'Có sao kê tài khoản chung', impact: 18 },
        { factor: 'Có hóa đơn mua BĐS', impact: 12 },
        { factor: 'Không có hợp đồng đóng góp', impact: -8 },
      ],
    },
    strategySuggestions: [
      'Thu thập sao kê ngân hàng giai đoạn 2018-2023',
      'Yêu cầu định giá độc lập tài sản tranh chấp',
      'Chuẩn bị nhân chứng xác nhận nguồn vốn chung',
    ],
  }
}

export interface SyncModulesPayload {
  analysis?: { timeline: AnalysisTimelineItem[]; conflicts: AnalysisConflict[] }
  drafting?: { latestVersion: string | null }
  strategy?: { riskForecast: RedTeamResult['riskForecast'] | null; suggestions: string[] }
}

export interface SyncWorkspaceResult {
  success: boolean
  syncedAt: string
  eCourt: {
    caseUrl: string
    documentsUploaded: number
    timelineEvents: number
    flaggedRisks: number
  }
  mockTrialReady: boolean
}

export async function syncWorkspaceToECourt(caseId: string, modules: SyncModulesPayload): Promise<SyncWorkspaceResult> {
  const payload = {
    caseId,
    timeline: modules.analysis?.timeline ?? [],
    conflicts: modules.analysis?.conflicts ?? [],
    draftDocument: modules.drafting?.latestVersion ?? null,
    riskReport: modules.strategy?.riskForecast ?? null,
    strategy: modules.strategy?.suggestions ?? [],
  }
  await simulateProcessing(1500)
  return {
    success: true,
    syncedAt: new Date().toISOString(),
    eCourt: {
      caseUrl: `/cases/${caseId}`,
      documentsUploaded: 3,
      timelineEvents: payload.timeline.length,
      flaggedRisks: payload.conflicts.length,
    },
    mockTrialReady: payload.timeline.length > 0 && payload.riskReport !== null,
  }
}

export function generateAIResponses(caseId: string, scenario: string, aiRole: string): string[] {
  return [
    `[${caseId}] Phản hồi ${aiRole} — kịch bản ${scenario}: Đề nghị làm rõ nguồn gốc tài sản theo hồ sơ đã đồng bộ.`,
    'Câu hỏi bổ sung: Bạn có chứng cứ sao kê giai đoạn 2018–2023 không?',
  ]
}

export interface MockTrialResult {
  sessionId: string
  scenario: string
  aiResponses: string[]
  tips: string[]
}

export async function startMockTrial(caseId: string, scenario: string, aiRole: string): Promise<MockTrialResult> {
  await simulateProcessing(1000)
  return {
    sessionId: `mock-${caseId}-${Date.now()}`,
    scenario,
    aiResponses: generateAIResponses(caseId, scenario, aiRole),
    tips: [
      'Hãy dẫn chiếu Điều 33 Luật HN&GĐ 2014 khi tranh luận về tài sản chung',
      'Chuẩn bị sẵn câu trả lời cho câu hỏi về nguồn gốc vốn ban đầu',
    ],
  }
}
